import React, { useState, useEffect } from "react";
import useWindowSize from "../../hooks/useWindowSize";

// css
import "./Performance.css";

// components
import PerformanceHeaderCard from "./PerformanceHeaderCard";
import TotalReturns from "./TotalReturns";
import MonthlyTotalReturns from "./MonthlyTotalReturns";
import HistoricNavChart from "./charts/HistoricNavChart";
import GrowthChart from "./charts/GrowthChart";
import HistoricDistChart from "./charts/HistoricalDistributionChart";

const PerformanceHeader = () => {
  const prodKey = process.env.REACT_APP_PRODUCT_KEY;
  const prUrl = process.env.REACT_APP_PUBLICRATES_URL;
  const url = `${prUrl}${prodKey}_share_class_performance.json`;
  const { width } = useWindowSize();

  const [fetchedData, setFetchedData] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showChartDropdown, setShowChartDropdown] = useState(false);
  const [classOption, setClassOption] = useState("Select Share Class");
  const [ayp, setAyp] = useState("");
  const [navAmount, setNavAmount] = useState("");
  const [navDate, setNavDate] = useState("");
  const [totalReturns, setTotalReturns] = useState();
  const [monthlyTotalReturns, setMonthlyTotalReturns] = useState(null);
  const [salesLoad, setSalesLoad] = useState(null);
  const [annualNavs, setAnnualNavs] = useState(null);
  const [annualGrowths, setAnnualGrowths] = useState(null);
  const [annualDist, setAnnualDist] = useState(null);
  const [activeTab, setActiveTab] = useState("growth"); // default tab

  async function fetchData() {
    const response = await fetch(url);
    if (!response.ok) {
      const message = `An error has occured: ${response.status}`;
      throw new Error(message);
    }
    const data = await response.json();
    const filterableData = data.product["share-class"];
    // console.log(data.product["share-class"]);
    console.log(data);

    setFetchedData(data.product["share-class"]);

    if (filterableData && Array.isArray(filterableData)) {
      const classIItems = filterableData.filter(
        (item) => item.share_class_name === "Class I"
      );
      classIItems.map((items) => {
        // console.log(items);
        setAyp(items.annualized_yield_percent);
        setNavAmount(items.nav_amount);
        setNavDate(items.nav_date);
        setClassOption(items.share_class_name);
        setTotalReturns(items["latest-month-returns"]);
        setMonthlyTotalReturns(items["annual-returns"]);
        setAnnualNavs(items["annual-navs"]);
        setAnnualGrowths(items["annual-growths"]);
        setAnnualDist(items["annual-share-dists"]);
        setSalesLoad(items.sales_load_percent);
      });
    } else if (
      filterableData &&
      filterableData.share_class_name === "Class I"
    ) {
      setAyp(filterableData.annualized_yield_percent);
      setNavAmount(filterableData.nav_amount);
      setNavDate(filterableData.nav_date);
      setClassOption(filterableData.share_class_name);
      setTotalReturns(filterableData["latest-month-returns"]);
      setMonthlyTotalReturns(filterableData["annual-returns"]);
      setAnnualNavs(filterableData["annual-navs"]);
      setSalesLoad(filterableData.sales_load_percent);
    }
  }

  // run the data fetch on load
  useEffect(() => {
    fetchData();
  }, []);

  const handleStateClick = (
    e,
    ayp,
    nAmount,
    nDate,
    sName,
    totalReturns,
    monthlyReturns,
    salesLoadP,
    annualNavs
  ) => {
    e.preventDefault();
    // console.log("clicked");
    setAyp(ayp);
    setNavAmount(nAmount);
    setNavDate(nDate);
    setClassOption(sName);
    setTotalReturns(totalReturns);
    setMonthlyTotalReturns(monthlyReturns);
    setSalesLoad(salesLoadP);
    setAnnualNavs(annualNavs);
    setShowDropdown();
  };

  const renderTabSection = () => {
    return (
      <div className="chart-tabs-container px-20 pt-20">
        <div className="main-margin">
          <div className="chart-tabs w-full">
            <span
              className={activeTab === "growth" ? "c-tab active" : "c-tab"}
              onClick={() => setActiveTab("growth")}
            >
              Growth of $100K<sup>1,3</sup> (Class I)
            </span>
            <span className="text-2xl">|</span>
            <span
              className={activeTab === "nav" ? "c-tab active" : "c-tab"}
              onClick={() => setActiveTab("nav")}
            >
              Historical NAV Per Share<sup>1,4</sup>
            </span>
            <span className="text-2xl">|</span>
            <span
              className={activeTab === "dist" ? "c-tab active" : "c-tab"}
              onClick={() => setActiveTab("dist")}
            >
              Historical Distributions Per Share<sup>2</sup>
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileChartSection = () => {
    return (
      <div className="px-7 md:px-20 pt-20">
        <div className="main-margin">
          <div className="relative inline-block w-full max-w-xs">
            <button
              onClick={() => setShowChartDropdown(!showChartDropdown)}
              className="w-full bg-white text-[#C39F5C] border border-[#C39F5C] px-4 py-2 rounded text-left" 
            >
              {activeTab === "growth"
                ? "Growth of $100K (Class I)"
                : activeTab === "nav"
                ? "Historical NAV Per Share"
                : "Historical Distributions Per Share"}
              <span className="float-right">&#9660;</span>
            </button>
            {showChartDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow">
                <p
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    activeTab === "growth" ? "text-[#C39F5C] font-semibold" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("growth");
                    setShowChartDropdown(false);
                  }}
                >
                  Growth of $100K<sup>1,3</sup> (Class I)
                </p>
                <p
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    activeTab === "nav" ? "text-[#C39F5C] font-semibold" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("nav");
                    setShowChartDropdown(false);
                  }}
                >
                  Historical NAV Per Share<sup>1,4</sup>
                </p>
                <p
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                    activeTab === "dist" ? "text-[#C39F5C] font-semibold" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("dist");
                    setShowChartDropdown(false);
                  }}
                >
                  Historical Distributions Per Share<sup>2</sup>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <section className="performance-header-container" id="performance-header">
        <div className="main-padding">
          <div className="main-margin">
            <div className="performance-header-content-wrapper">
              <h1 class="main-header t-white">Performance</h1>
              <div class="dropdown" style={{ width: "220px" }}>
                <button
                  class="dropbtn"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <span>{classOption}</span> <span>&#9660;</span>
                </button>
                <div
                  class={
                    showDropdown === true
                      ? "dropdown-content-show"
                      : "dropdown-content-hide"
                  }
                >
                  {fetchedData
                    ? fetchedData.map((sClass, i) => {
                        // console.log(typeof(sClass.sales_load_percent))
                        return (
                          <p
                            key={sClass.state_id}
                            onClick={(e) => {
                              handleStateClick(
                                e,
                                sClass.annualized_yield_percent,
                                sClass.nav_amount,
                                sClass.nav_date,
                                sClass.share_class_name,
                                sClass["latest-month-returns"],
                                sClass["annual-returns"],
                                sClass.sales_load_percent,
                                sClass["annual-navs"]
                              );
                              setShowDropdown(false);
                            }}
                          >
                            {sClass.share_class_name}
                          </p>
                        );
                      })
                    : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <PerformanceHeaderCard ayp={ayp} nAmount={navAmount} nDate={navDate} />
      {!ayp ? <div style={{ padding: "100px 0" }}></div> : null}
      {ayp ? (
        <TotalReturns
          data={totalReturns}
          class={classOption}
          load={salesLoad}
        />
      ) : null}
      {ayp ? (
        <MonthlyTotalReturns
          data={monthlyTotalReturns}
          class={classOption}
          load={salesLoad}
        />
      ) : null}
      {ayp && width > 1188 ? renderTabSection() : renderMobileChartSection()}
      {ayp && activeTab === "nav" ? (
        <HistoricNavChart
          data={annualNavs}
          classOpt={classOption}
          load={salesLoad}
        />
      ) : null}

      {ayp && activeTab === "growth" ? (
        <GrowthChart
          data={annualGrowths}
          classOpt={classOption}
          load={salesLoad}
        />
      ) : null}

      {ayp && activeTab === "dist" ? (
        <HistoricDistChart
          data={annualDist}
          classOpt={classOption}
          load={salesLoad}
        />
      ) : null}
    </>
  );
};

export default PerformanceHeader;
